import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useRef, useState } from "react";
import { Image, Platform, Pressable, ScrollView, Text, View, type DimensionValue, type ViewStyle } from "react-native";

import { getAdminJudgeDetail, type AdminJudgeDetail } from "../../../../lib/judges";
import { RADII, SPACING, STROKES } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";
import {
  AdminAvatar,
  AdminButton,
  AdminError,
  AdminLoading,
  AdminPageHeader,
  ListRow,
  ListRowGroup,
  makeInitials,
} from "../../../../components/admin/ui";
import { SectionHeader } from "../../../../components/aspects/SectionHeader";
import { StatusPill, type StatusPillTone } from "../../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;
const TZ = "America/New_York";

const STATUS_TONE: Record<AdminJudgeDetail["status"], StatusPillTone> = {
  active: "sage",
  inactive: "grey",
  pending_invite: "amber",
};

const STATUS_TEXT: Record<AdminJudgeDetail["status"], string> = {
  active: "Active",
  inactive: "Inactive",
  pending_invite: "Pending invite",
};

function fmtTs(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ })} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: TZ })}`;
}

export default function AdminJudgeDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const userId = typeof id === "string" ? id : Array.isArray(id) ? id[0] : "";
  const [detail, setDetail] = useState<AdminJudgeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!userId) return;
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      setDetail(await getAdminJudgeDetail(userId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load judge");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [userId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", backgroundColor: THEME.pageBg }}>
        <AdminLoading theme={THEME} label="Loading judge…" />
      </View>
    );
  }

  if (err || !detail) {
    return (
      <View style={{ flex: 1, padding: SPACING.xl, backgroundColor: THEME.pageBg, gap: 12 }}>
        <AdminError theme={THEME} message={err ?? "Judge not found."} onRetry={() => void load()} />
        <AdminButton theme={THEME} label="Back to judges" variant="outline" size="sm" onPress={() => router.replace("/admin/judges" as Href)} />
      </View>
    );
  }

  const displayName = detail.full_name ?? detail.email;

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 40 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 880 }}>
        <AdminPageHeader theme={THEME} eyebrow="CRM · Judges" title={displayName} />
        <View style={{ flexDirection: "row", marginTop: -SPACING.md, marginBottom: SPACING.md }}>
          <StatusPill theme={THEME} label={STATUS_TEXT[detail.status]} tone={STATUS_TONE[detail.status]} />
        </View>

        {/* Profile */}
        <SectionHeader theme={THEME} title="Profile" tone="dark" />
        <View style={{ padding: 18, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border, flexDirection: "row", gap: 16, flexWrap: "wrap" }}>
          {detail.avatar_url ? (
            <Image source={{ uri: detail.avatar_url }} style={{ width: 80, height: 80, borderRadius: RADII.full }} />
          ) : (
            <AdminAvatar theme={THEME} name={detail.full_name ?? detail.email} size={80} fallback={makeInitials(detail.full_name ?? detail.email)} />
          )}
          <View style={{ flex: 1, minWidth: 220 }}>
            <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 14, color: THEME.textOnSurface }}>{detail.email}</Text>
            {detail.headline ? <Text style={{ marginTop: 4, fontFamily: THEME.bodyMediumFont, fontSize: 12.5, color: THEME.textOnSurfaceDim }}>{detail.headline}</Text> : null}
            {detail.organization ? <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>{detail.organization}</Text> : null}
            {detail.bio ? (
              <Text style={{ marginTop: 10, fontFamily: THEME.bodyFont, fontSize: 12.5, lineHeight: 18, color: THEME.textOnSurfaceDim }}>{detail.bio}</Text>
            ) : (
              <Text style={{ marginTop: 10, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceFaint, fontStyle: "italic" }}>No bio on file.</Text>
            )}
            {detail.areas_of_expertise.length > 0 ? (
              <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                {detail.areas_of_expertise.map((tag) => (
                  <StatusPill key={tag} theme={THEME} label={tag} tone="amber" showDot={false} />
                ))}
              </View>
            ) : null}
            {detail.linkedin_url ? (
              <Text style={{ marginTop: 10, fontFamily: THEME.bodyMediumFont, fontSize: 11.5, color: THEME.accent }} selectable>{detail.linkedin_url}</Text>
            ) : null}
          </View>
        </View>

        {/* Assignments */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Assignments" tone="dark" />
          {detail.assignments.length === 0 ? (
            <Card>No assignments yet.</Card>
          ) : (
            <View style={{ gap: 8 }}>
              {detail.assignments.map((a) => {
                const aTone: StatusPillTone = a.status === "active" ? "sage" : a.status === "removed" ? "grey" : "amber";
                const pct = a.total_assigned > 0 ? Math.min(100, Math.round((a.scored_count / a.total_assigned) * 100)) : 0;
                return (
                  <Pressable
                    key={a.judge_id}
                    onPress={() => router.push(`/client/competition/${a.competition_id}` as Href)}
                    style={[
                      { padding: 14, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <Text style={{ flex: 1, minWidth: 200, fontFamily: THEME.bodyMediumFont, fontSize: 13, color: THEME.textOnSurface }} numberOfLines={1}>
                        {a.competition_name}
                      </Text>
                      <StatusPill theme={THEME} label={a.status} tone={aTone} showDot={false} />
                    </View>
                    {a.company_name ? <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>{a.company_name}</Text> : null}
                    <Text style={{ marginTop: 6, fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>
                      {a.scored_count} / {a.total_assigned} scored{a.invited_at ? ` · invited ${fmtTs(a.invited_at)}` : ""}
                    </Text>
                    <View style={{ marginTop: 6, height: 4, borderRadius: 2, backgroundColor: THEME.altBg, overflow: "hidden" }}>
                      <View style={{ width: `${pct}%` as DimensionValue, height: 4, backgroundColor: THEME.accent }} />
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}
        </View>

        {/* Activity */}
        <View style={{ marginTop: SPACING.lg }}>
          <SectionHeader theme={THEME} title="Activity" tone="dark" />
          {detail.activity.length === 0 ? (
            <Card>No score activity in the last 50 events.</Card>
          ) : (
            <ListRowGroup theme={THEME}>
              {detail.activity.map((a, idx) => (
                <ListRow
                  key={a.submission_score_id}
                  theme={THEME}
                  first={idx === 0}
                  leading={<View style={{ width: 6, height: 6, borderRadius: RADII.full, backgroundColor: a.status === "submitted" ? THEME.success : THEME.accent }} />}
                  title={a.competition_name}
                  subtitle={`${a.status === "submitted" ? "Score finalized" : "Draft saved"}${a.score != null ? ` · ${a.score}/10` : ""}`}
                  meta={fmtTs(a.updated_at)}
                />
              ))}
            </ListRowGroup>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

function Card({ children }: { children: string }) {
  return (
    <View style={{ padding: 18, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border }}>
      <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>{children}</Text>
    </View>
  );
}
